import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/Add';
import FacebookIcon from '@mui/icons-material/Facebook';
import ReactMarkdown from 'react-markdown';

import {
  deleteNews,
  listNews,
  newsImageUrl,
  NewsPost,
  restoreNews,
} from '../../api';
import { useAuth } from '../../auth/AuthContext';
import NewsEditDialog from '../dialogs/NewsEditDialog';

const POSTS_PER_PAGE = 5;

// Tombstone palette: muted red tint over the regular `#fafafa` page
// background, plus desaturated text. Bright enough to register at a
// glance, light enough that the card still reads.
const DELETED_BG = '#fdecec';
const DELETED_BORDER = '#f2c7c7';
const DELETED_OPACITY = 0.55;

type DialogState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; post: NewsPost };

const formatPostDate = (raw: string): string => {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const normalizeBody = (body: string): string => body.replace(/\n+/g, '\n\n');

// Post-list ordering: newest first by date, ties broken by id desc to
// match the server's `ORDER BY posted_on DESC, id DESC` so a locally
// created post lands in the same slot it would after a refetch.
const sortPosts = (posts: NewsPost[]): NewsPost[] =>
  [...posts].sort((a, b) => {
    if (a.date !== b.date) return b.date.localeCompare(a.date);
    return b.id.localeCompare(a.id);
  });

const NewsPage = () => {
  const { idToken } = useAuth();
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  // Per-id "busy" flag for the delete/restore buttons so a slow
  // network doesn't let the operator double-click.
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    // Wait for Firebase to hand us a token — the server widens the
    // visible set (to include soft-deleted rows) only when an admin
    // token is on the request. Fetching anonymously first and again
    // after the token arrives would briefly hide deleted rows.
    if (!idToken) return;
    let cancelled = false;
    (async () => {
      try {
        // The backend already returns newest-first via
        // `ORDER BY posted_on DESC, id DESC` so no re-sort is needed
        // here. limit=200 is generous — paginate server-side once the
        // archive grows past that.
        const response = await listNews(idToken, 200, 0);
        if (!cancelled) setPosts(response.items);
      } catch {
        // Leave posts empty.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  const totalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));
  const pageStart = page * POSTS_PER_PAGE;
  const visible = useMemo(
    () => posts.slice(pageStart, pageStart + POSTS_PER_PAGE),
    [posts, pageStart],
  );

  const upsertPost = (saved: NewsPost) => {
    setPosts((prev) => {
      const exists = prev.some((p) => p.id === saved.id);
      const next = exists
        ? prev.map((p) => (p.id === saved.id ? saved : p))
        : [saved, ...prev];
      return sortPosts(next);
    });
  };

  const handleDelete = async (post: NewsPost) => {
    if (!idToken) return;
    setPendingId(post.id);
    try {
      const updated = await deleteNews(idToken, post.id);
      upsertPost(updated);
    } catch {
      // Soft-delete is non-destructive; on failure leave the card
      // alone and let the operator try again. A toast/snackbar can
      // come in a future tune.
    } finally {
      setPendingId(null);
    }
  };

  const handleRestore = async (post: NewsPost) => {
    if (!idToken) return;
    setPendingId(post.id);
    try {
      const updated = await restoreNews(idToken, post.id);
      upsertPost(updated);
    } catch {
      // Same posture as delete — silent failure for now.
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#fafafa', minHeight: '70vh' }}>
      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
          sx={{ mb: 6 }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ letterSpacing: '0.2em', color: '#64748b' }}
            >
              News
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
              From the shop
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              Store updates, new arrivals, and the occasional rant.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialog({ kind: 'create' })}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            New post
          </Button>
        </Stack>

        {loaded && posts.length === 0 && (
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Nothing to share just yet — click "New post" to start.
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {visible.map((post, idx) => {
            const firstImage = post.images?.[0];
            const isLast = idx === visible.length - 1;
            const isDeleted = !!post.deletedAt;
            const isPending = pendingId === post.id;
            return (
              <Box
                key={post.id}
                id={post.id}
                sx={{
                  mt: idx === 0 ? 0 : 6,
                  pb: isLast ? 0 : 6,
                  borderBottom: isLast ? 'none' : '1px solid #e5e7eb',
                }}
              >
                {/* Content block — gets the tombstone treatment when
                    the post is soft-deleted. The action row below is
                    kept outside this Box so Restore stays clickable. */}
                <Box
                  aria-disabled={isDeleted}
                  sx={{
                    ...(isDeleted && {
                      backgroundColor: DELETED_BG,
                      border: `1px solid ${DELETED_BORDER}`,
                      borderRadius: 2,
                      p: 3,
                      opacity: DELETED_OPACITY,
                      pointerEvents: 'none',
                    }),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      display: 'block',
                      mb: firstImage ? 2 : 1.5,
                    }}
                  >
                    {formatPostDate(post.date)}
                    {isDeleted && (
                      <Box
                        component="span"
                        sx={{ color: '#b91c1c', ml: 1, fontWeight: 600 }}
                      >
                        · deleted
                      </Box>
                    )}
                  </Typography>
                  {firstImage && (
                    <Box
                      component="img"
                      src={newsImageUrl(firstImage)}
                      alt=""
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        mb: 3,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      color: '#1f2937',
                      fontSize: '1rem',
                      lineHeight: 1.75,
                      '& p': { m: 0, mb: 1.5, '&:last-child': { mb: 0 } },
                      '& ul, & ol': { my: 1.5, pl: 3 },
                      '& li': { mb: 0.5 },
                      '& strong': { fontWeight: 700 },
                      '& em': { fontStyle: 'italic' },
                      '& a': { color: '#0f172a', textDecoration: 'underline' },
                      '& h1, & h2, & h3, & h4': {
                        fontWeight: 700,
                        fontSize: '1.125rem',
                        m: 0,
                        mt: 2,
                        mb: 1,
                      },
                      '& code': {
                        backgroundColor: '#f1f5f9',
                        px: 0.5,
                        borderRadius: 0.5,
                        fontSize: '0.9em',
                      },
                      '& blockquote': {
                        borderLeft: '3px solid #cbd5e1',
                        pl: 2,
                        ml: 0,
                        my: 2,
                        color: '#475569',
                        fontStyle: 'italic',
                      },
                    }}
                  >
                    <ReactMarkdown>{normalizeBody(post.body)}</ReactMarkdown>
                  </Box>
                  {post.facebook && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        component="a"
                        href={post.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        aria-label="View on Facebook"
                        sx={{ color: '#94a3b8', '&:hover': { color: '#1877f2' } }}
                      >
                        <FacebookIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* Action row — Edit/Delete for live posts, Restore
                    for tombstones. Sits outside the dimmed content
                    block so it remains usable in both states. */}
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ mt: 2 }}
                >
                  {isDeleted ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isPending}
                      onClick={() => handleRestore(post)}
                      sx={{ textTransform: 'none' }}
                    >
                      {isPending ? 'Restoring…' : 'Restore'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setDialog({ kind: 'edit', post })}
                        sx={{ textTransform: 'none' }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        disabled={isPending}
                        onClick={() => handleDelete(post)}
                        sx={{ textTransform: 'none' }}
                      >
                        {isPending ? 'Deleting…' : 'Delete'}
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>

        {posts.length > POSTS_PER_PAGE && (
          <Box
            sx={{
              mt: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              sx={{ textTransform: 'none' }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Page {page + 1} of {totalPages}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              sx={{ textTransform: 'none' }}
            >
              Next
            </Button>
          </Box>
        )}
      </Container>

      <NewsEditDialog
        open={dialog.kind !== 'closed'}
        // The dialog reads `mode` on every open, so a placeholder
        // `kind: 'create'` while closed is harmless — `open={false}`
        // short-circuits the seeding effect anyway.
        mode={dialog.kind === 'closed' ? { kind: 'create' } : dialog}
        onClose={() => setDialog({ kind: 'closed' })}
        onSaved={(post) => {
          upsertPost(post);
          // If the operator just created a post, jump to page 0 so
          // they see it immediately instead of paging from where the
          // edit happened.
          if (dialog.kind === 'create') setPage(0);
        }}
      />
    </Box>
  );
};

export default NewsPage;
